import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import React from 'react'
import type {
    ApplyV2Question,
    ApplyV2SettingsSectionName,
    GetAllQuestionsV2Response,
    NavItemsState,
} from '@core/app/types/applyV2Type'
import ApplyV2ReviewApplySkills from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplySkills'
import ApplyV2Accordion from '@core/components/ApplyDialogV2/ApplyV2Accordion'
import ApplyV2ReviewApplyCv from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyCv'
import ApplyV2ReviewApplyProfile from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyProfile'
import ApplyV2ReviewApplyQuestions from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyQuestions'
import ApplyV2ReviewApplyEducation from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyEducation'
import ApplyV2ReviewApplyExperience from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyExperience'
import ApplyV2ReviewApplyCourse from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyCourse'
import ApplyV2ReviewApplyLanguages from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyLanguages'
import ApplyV2ReviewApplyDriverLicense from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyDriverLicense'
import ApplyV2ReviewApplyReference from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem/ApplyV2ReviewApplyReference'

import './ApplyV2ReviewApplyStepItem.scss'

interface ApplyV2ReviewApplyStepItemProps {
    className?: string
    fieldType: ApplyV2SettingsSectionName
    title?: string
    fieldState?: NavItemsState
    isValidateLoading?: boolean
    isShowExpandButton?: boolean
    isQuestionsLoading?: boolean
    questionsData?: ApplyV2Question[] | undefined | null
}

const baseClass = 'ApplyV2ReviewApplyStepItem'

const componentsMap: Partial<
    Record<
        ApplyV2SettingsSectionName,
        React.FC<{
            className?: string
            questionsData?: ApplyV2Question[] | undefined | null
            isQuestionsLoading?: boolean
        }>
    >
> = {
    cv: ApplyV2ReviewApplyCv,
    profile: ApplyV2ReviewApplyProfile,
    question: ApplyV2ReviewApplyQuestions,
    education: ApplyV2ReviewApplyEducation,
    workExperience: ApplyV2ReviewApplyExperience,
    course: ApplyV2ReviewApplyCourse,
    language: ApplyV2ReviewApplyLanguages,
    driverLicence: ApplyV2ReviewApplyDriverLicense,
    reference: ApplyV2ReviewApplyReference,
    skill: ApplyV2ReviewApplySkills,
    // reviewApply: ApplyV2ReviewApplyReview,
}

function ApplyV2ReviewApplyStepItem({
    className,
    fieldType,
    title = '',
    fieldState = 'default',
    isValidateLoading = false,
    isShowExpandButton = true,
    isQuestionsLoading = false,
    questionsData,
}: ApplyV2ReviewApplyStepItemProps) {
    const Component = componentsMap[fieldType]

    if (!Component) return null

    const getIcon = (state: NavItemsState) => {
        if (isValidateLoading) {
            return <ApplySkeleton isLoading={true} style={{ width: '16px', height: '16px', borderRadius: '50%' }} />
        }
        switch (state) {
            case 'error':
                return (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_1072_46751)">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0.733398 8.00007C0.733398 3.9868 3.9868 0.733398 8.00007 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00007C15.2667 12.0133 12.0133 15.2667 8.00007 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00007ZM8.00008 9.26673C7.66871 9.26673 7.40008 8.9981 7.40008 8.66673V4.66673C7.40008 4.33536 7.66871 4.06673 8.00008 4.06673C8.33145 4.06673 8.60008 4.33536 8.60008 4.66673V8.66673C8.60008 8.9981 8.33145 9.26673 8.00008 9.26673ZM8.00008 11.3334C8.36827 11.3334 8.66675 11.0349 8.66675 10.6667C8.66675 10.2985 8.36827 10.0001 8.00008 10.0001C7.63189 10.0001 7.33341 10.2985 7.33341 10.6667C7.33341 11.0349 7.63189 11.3334 8.00008 11.3334Z"
                                fill="#B62626"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_1072_46751">
                                <rect width="16" height="16" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                )
            case 'success':
                return (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_4546_30543)">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.00104 0.734375C3.98777 0.734375 0.734375 3.98777 0.734375 8.00104C0.734375 12.0143 3.98777 15.2677 8.00104 15.2677C12.0143 15.2677 15.2677 12.0143 15.2677 8.00104C15.2677 3.98777 12.0143 0.734375 8.00104 0.734375ZM11.0846 6.76593C11.3229 6.53571 11.3295 6.15587 11.0993 5.91753C10.8691 5.67918 10.4892 5.6726 10.2509 5.90281L7.21674 8.83351L5.75124 7.41797C5.51289 7.18775 5.13305 7.19434 4.90283 7.43268C4.67262 7.67102 4.6792 8.05086 4.91755 8.28108L6.7999 10.0993C7.03242 10.3239 7.40107 10.3239 7.63359 10.0993L11.0846 6.76593Z"
                                fill="#006D33"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_4546_30543">
                                <rect width="16" height="16" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                )
            default:
                return (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_2726_42457)">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M1.93438 8.00104C1.93438 4.65051 4.65051 1.93438 8.00104 1.93438C11.3516 1.93438 14.0677 4.65051 14.0677 8.00104C14.0677 11.3516 11.3516 14.0677 8.00104 14.0677C4.65051 14.0677 1.93438 11.3516 1.93438 8.00104ZM8.00104 0.734375C3.98777 0.734375 0.734375 3.98777 0.734375 8.00104C0.734375 12.0143 3.98777 15.2677 8.00104 15.2677C12.0143 15.2677 15.2677 12.0143 15.2677 8.00104C15.2677 3.98777 12.0143 0.734375 8.00104 0.734375Z"
                                fill="#77777C"
                            ></path>
                        </g>
                        <defs>
                            <clipPath id="clip0_2726_42457">
                                <rect width="16" height="16" fill="white"></rect>
                            </clipPath>
                        </defs>
                    </svg>
                )
        }
    }

    return (
        <ApplyV2Accordion
            title={title}
            className={`${baseClass} ${className ? ` ${className}` : ''}`}
            icon={getIcon(fieldState)}
            fieldState={fieldState}
            // isShowExpandButton={isShowExpandButton}
        >
            <Component
                className={`${baseClass}__content`}
                isQuestionsLoading={isQuestionsLoading}
                questionsData={questionsData}
            />
        </ApplyV2Accordion>
    )
}

export default ApplyV2ReviewApplyStepItem
